import { ConfigurationType, SystemCombinationType } from 'data/types';
import { zoneRequiresPlacedAirHandler } from 'utils/configuration-zone-equipment';

// A PTAC design is complete once every unit sits on the plan with its room:
// a packaged terminal unit IS the whole system, so there is no air handler
// or line set to place.
export function isPtacDesignComplete(config: ConfigurationType): boolean {
  return (
    config.condensers.length > 0 &&
    config.condensers.every(
      (unit) =>
        unit.position !== undefined &&
        unit.zones.some((zone) => zone.zoneItems.length > 0)
    )
  );
}

export function isConfigurationComplete(
  config: ConfigurationType | undefined,
  leftoverSpaceIds?: ReadonlySet<string>
): boolean {
  if (!config) return false;

  const positionedCondensers = config.condensers.filter(
    (condenser) => condenser.position !== undefined
  );
  if (positionedCondensers.length === 0) return false;

  const everyPositionedCondenserHasZonedSpaces = positionedCondensers.every(
    (condenser) => condenser.zones.some((zone) => zone.zoneItems.length > 0)
  );
  if (!everyPositionedCondenserHasZonedSpaces) return false;

  const zonesWithItems = config.condensers
    .flatMap((condenser) => condenser.zones)
    .filter((zone) => zone.zoneItems.length > 0);

  return zonesWithItems.every((zone) => {
    if (!zoneRequiresPlacedAirHandler(zone, leftoverSpaceIds)) return true;
    return (
      zone.airHandler !== undefined &&
      (config.lineSets?.some(
        (lineSet) => lineSet.airHandlerUniqueId === zone.airHandler?.uniqueId
      ) ??
        false)
    );
  });
}

export function configurationHasValidSystems(
  config: ConfigurationType,
  systemCombinationsFull: Record<string, SystemCombinationType[]>
): boolean {
  return (systemCombinationsFull[config.uniqueId]?.length ?? 0) > 0;
}

export function isPtacDesignReady(
  config: ConfigurationType | undefined
): boolean {
  return !!config && isPtacDesignComplete(config) && config.valid !== false;
}

export function isConfigurationEligible(
  config: ConfigurationType | undefined,
  systemCombinationsFull: Record<string, SystemCombinationType[]>,
  leftoverSpaceIds?: ReadonlySet<string>
): boolean {
  if (!config) return false;
  return (
    isConfigurationComplete(config, leftoverSpaceIds) &&
    configurationHasValidSystems(config, systemCombinationsFull)
  );
}

export function getEligibleConfigurations(
  configurations: ConfigurationType[],
  systemCombinationsFull: Record<string, SystemCombinationType[]>,
  leftoverSpaceIds?: ReadonlySet<string>
): ConfigurationType[] {
  return configurations.filter((config) =>
    isConfigurationEligible(config, systemCombinationsFull, leftoverSpaceIds)
  );
}

export function configurationHasBeenFetched(
  config: ConfigurationType,
  systemCombinationsFull: Record<string, SystemCombinationType[]>
): boolean {
  return Object.prototype.hasOwnProperty.call(
    systemCombinationsFull,
    config.uniqueId
  );
}

export function getFetchedConfigurations(
  configurations: ConfigurationType[],
  systemCombinationsFull: Record<string, SystemCombinationType[]>,
  leftoverSpaceIds?: ReadonlySet<string>
): ConfigurationType[] {
  return configurations.filter(
    (config) =>
      isConfigurationComplete(config, leftoverSpaceIds) &&
      configurationHasBeenFetched(config, systemCombinationsFull)
  );
}
